import React, { useEffect } from "react";
import { motion } from "framer-motion";
import { useLang } from "../context/LanguageContext";
import { useSeo } from "../hooks/useSeo";
import LeadForm from "../components/LeadForm";
import { Mail, Newspaper, MapPin } from "lucide-react";

export default function Kontakt() {
  const { t, lang } = useLang();
  useSeo({ title: t.seo.kontakt.title, description: t.seo.kontakt.description, path: "/kontakt", lang });

  return (
    <main data-testid="kontakt-page" className="bg-ink text-cream pt-20 min-h-screen">
      <section className="relative overflow-hidden grain">
        <div className="absolute inset-0 bay-lines opacity-30 pointer-events-none" />
        <div
          className="absolute -top-20 right-0 w-[500px] h-[500px] rounded-full pointer-events-none"
          style={{ background: "radial-gradient(circle, rgba(232,168,0,0.12) 0%, rgba(232,168,0,0) 70%)" }}
        />

        <div className="max-w-7xl mx-auto px-6 md:px-10 pt-12 pb-20 md:pt-20 md:pb-28 relative">
          <motion.span
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 0.4 }}
            className="inline-flex items-center gap-2 border border-amber/40 px-3 py-1.5 text-xs font-bold tracking-widest uppercase text-amber"
          >
            <span className="w-1.5 h-1.5 bg-amber rounded-full" />
            {t.kontakt.heroEyebrow}
          </motion.span>

          <motion.h1
            initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5, delay: 0.05 }}
            className="mt-7 font-display text-5xl sm:text-7xl md:text-8xl font-extrabold leading-[1.02] tracking-tighter"
            style={{ letterSpacing: "-0.04em" }}
            data-testid="kontakt-headline"
          >
            {t.kontakt.heroTitle}
          </motion.h1>

          <motion.p
            initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5, delay: 0.15 }}
            className="mt-7 text-base md:text-xl text-cream/70 max-w-2xl leading-relaxed"
          >
            {t.kontakt.heroSub}
          </motion.p>

          <div className="mt-14 md:mt-20 grid md:grid-cols-12 gap-10 md:gap-16">
            {/* Info column */}
            <div className="md:col-span-5">
              <div className="border-t border-amber/30 pt-8 mb-10">
                <p className="text-base md:text-lg text-cream/80 leading-relaxed">{t.kontakt.info}</p>
              </div>

              <div className="space-y-7">
                <div className="flex items-start gap-4" data-testid="kontakt-email">
                  <div className="w-10 h-10 bg-amber-deep border border-amber/30 flex items-center justify-center shrink-0">
                    <Mail size={18} className="text-amber" />
                  </div>
                  <div>
                    <p className="text-xs font-bold tracking-widest uppercase text-amber/80 mb-1">{t.kontakt.emailLabel}</p>
                    <a href="mailto:hello@pendli.no" className="amber-link font-display text-lg font-bold">hello@pendli.no</a>
                  </div>
                </div>

                <div className="flex items-start gap-4" data-testid="kontakt-press">
                  <div className="w-10 h-10 bg-amber-deep border border-amber/30 flex items-center justify-center shrink-0">
                    <Newspaper size={18} className="text-amber" />
                  </div>
                  <div>
                    <p className="text-xs font-bold tracking-widest uppercase text-amber/80 mb-1">{t.kontakt.pressLabel}</p>
                    <a href="mailto:hello@pendli.no" className="amber-link font-display text-lg font-bold">hello@pendli.no</a>
                  </div>
                </div>

                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 bg-amber-deep border border-amber/30 flex items-center justify-center shrink-0">
                    <MapPin size={18} className="text-amber" />
                  </div>
                  <div>
                    <p className="text-xs font-bold tracking-widest uppercase text-amber/80 mb-1">Sted</p>
                    <p className="font-display text-lg font-bold">Asker, Norge · pendli.no</p>
                  </div>
                </div>
              </div>
            </div>

            {/* Form column */}
            <div className="md:col-span-7">
              <h2 className="font-display text-2xl md:text-4xl font-bold tracking-tight mb-6" style={{ letterSpacing: "-0.04em" }}>
                {t.kontakt.formTitle}
              </h2>
              <LeadForm
                role="contact"
                showMessage
                source="contact-page"
                testIdPrefix="kontakt-lead"
                successOverride={t.kontakt.successOverride}
              />
            </div>
          </div>
        </div>
      </section>
    </main>
  );
}
